"use client";

import Image from "next/image";

interface AlignmentContentProps {
	clientName?: string;
}

const buildTitle = (clientName?: string) => {
	if (!clientName) return "Momentum starts here";
	return `Momentum starts here for ${clientName}`;
};

export default function AlignmentContent({
	clientName,
}: AlignmentContentProps) {
	return (
		<div className="flex w-full flex-col gap-3 lg:h-[672px] lg:max-w-[448px]">
			{/* Meeting Details Card */}
			<div className="flex flex-1 flex-col rounded-[24px] border border-[#ebebeb] bg-[rgba(0,0,0,0.04)] p-1 lg:h-full">
				<div className="flex h-full flex-col items-center justify-center gap-1 overflow-clip rounded-[20px]">
					<div className="flex h-full w-full flex-col justify-between overflow-clip rounded-[20px] border border-[#ebebeb] bg-white p-8">
						<div className="flex w-full flex-col gap-4">
							<div className="flex w-full flex-col gap-6">
								<div className="flex w-full flex-col gap-4">
									{/* Avatar Group */}
									<div className="isolate flex items-center pr-3">
										<div className="relative z-[3] mr-[-12px] h-9 w-9">
											<Image
												src="/images/op-logo.svg"
												alt="Outpace Studios"
												width={36}
												height={36}
												className="rounded-full ring-1 ring-white"
											/>
										</div>
										<div className="relative z-[2] mr-[-12px] h-[30px] w-[30px]">
											<Image
												src="/images/avatar-jesse.jpg"
												alt="Team member"
												width={30}
												height={30}
												className="rounded-full ring-1 ring-white"
											/>
										</div>
										<div className="relative z-[1] h-[30px] w-[30px]">
											<Image
												src="/images/avatar-friso.jpg"
												alt="Team member"
												width={30}
												height={30}
												className="rounded-full ring-1 ring-white"
											/>
										</div>
									</div>
									{/* Title */}
									<p className="font-display text-2xl leading-[30px] font-bold tracking-[-0.01em] whitespace-pre-wrap text-[rgba(0,0,0,0.88)]">
										{buildTitle(clientName)}
									</p>
								</div>
							</div>
							{/* Description */}
							<p className="font-display text-sm leading-5 tracking-[0.02em] whitespace-pre-wrap text-[rgba(0,0,0,0.64)]">
								We partner with founders, product teams, and growth leaders to
								design and build products that drive fundraising, successful
								launches, and long-term growth.
							</p>
						</div>
						{/* Meeting Info */}
						<div className="mt-8 flex w-full flex-col gap-3 lg:mt-3">
							<div className="flex w-full flex-col gap-2">
								{/* Duration */}
								<div className="flex w-full items-center gap-2">
									<div className="h-4 w-4 overflow-clip">
										<svg
											width="16"
											height="16"
											viewBox="0 0 16 16"
											fill="none"
											xmlns="http://www.w3.org/2000/svg"
										>
											<path
												fillRule="evenodd"
												clipRule="evenodd"
												d="M7.99992 14.6668C11.6818 14.6668 14.6666 11.6821 14.6666 8.00016C14.6666 4.31826 11.6818 1.3335 7.99992 1.3335C4.31802 1.3335 1.33325 4.31826 1.33325 8.00016C1.33325 11.6821 4.31802 14.6668 7.99992 14.6668ZM8.66659 5.3335C8.66659 4.96531 8.36811 4.66683 7.99992 4.66683C7.63173 4.66683 7.33325 4.96531 7.33325 5.3335V8.00016C7.33325 8.17697 7.40349 8.34654 7.52851 8.47157L9.19518 10.1382C9.45553 10.3986 9.87764 10.3986 10.138 10.1382C10.3983 9.87788 10.3983 9.45577 10.138 9.19543L8.66659 7.72402V5.3335Z"
												fill="black"
												fillOpacity="0.56"
											/>
										</svg>
									</div>
									<p className="font-display flex-1 text-xs leading-4 font-semibold tracking-[0.01em] whitespace-pre-wrap text-[rgba(0,0,0,0.64)]">
										20 min
									</p>
								</div>
								{/* Platform */}
								<div className="flex w-full items-center gap-2">
									<div className="h-4 w-4">
										<svg
											width="16"
											height="16"
											viewBox="0 0 16 16"
											fill="none"
											xmlns="http://www.w3.org/2000/svg"
										>
											<g clipPath="url(#clip0_google_meet)">
												<path
													d="M9.48584 8.00015L10.9481 9.62515L12.9144 10.8468L13.2573 8.01015L12.9144 5.23682L10.9104 6.31015L9.48584 8.00015Z"
													fill="#00832D"
												/>
												<path
													d="M1 10.5835V13.0002C1 13.5527 1.46029 14.0002 2.02857 14.0002H4.51429L5.02857 12.1735L4.51429 10.5835L2.80857 10.0835L1 10.5835Z"
													fill="#0066DA"
												/>
												<path
													d="M4.51429 2L1 5.41667L2.80857 5.91667L4.51429 5.41667L5.02 3.84833L4.51429 2Z"
													fill="#E94235"
												/>
												<path
													d="M4.51429 5.4165H1V10.5832H4.51429V5.4165Z"
													fill="#2684FC"
												/>
												<path
													d="M15.1601 3.44685L12.9144 5.23685V10.8469L15.1704 12.6452C15.5081 12.9019 16.0018 12.6677 16.0018 12.2502V3.83352C16.0018 3.41102 15.497 3.17935 15.1601 3.44685ZM9.48583 8.00019V10.5835H4.5144V14.0002H11.8858C12.4541 14.0002 12.9144 13.5527 12.9144 13.0002V10.8469L9.48583 8.00019Z"
													fill="#00AC47"
												/>
												<path
													d="M11.8858 2H4.5144V5.41667H9.48583V8L12.9144 5.23833V3C12.9144 2.4475 12.4541 2 11.8858 2Z"
													fill="#FFBA00"
												/>
											</g>
											<defs>
												<clipPath id="clip0_google_meet">
													<rect
														width="15"
														height="12"
														fill="white"
														transform="translate(1 2)"
													/>
												</clipPath>
											</defs>
										</svg>
									</div>
									<p className="font-display flex-1 text-xs leading-4 font-semibold tracking-[0.01em] whitespace-pre-wrap text-[rgba(0,0,0,0.64)]">
										Google Meet
									</p>
								</div>
								{/* Social */}
								<div className="flex w-full items-center gap-2">
									<div className="h-4 w-4">
										<svg
											width="16"
											height="16"
											viewBox="0 0 16 16"
											fill="none"
											xmlns="http://www.w3.org/2000/svg"
										>
											<path
												fillRule="evenodd"
												clipRule="evenodd"
												d="M9.73159 2.67404L8.00095 1L6.27031 2.67404L3.88659 2.3367L3.47031 4.70777L1.34351 5.8367L2.40095 8L1.34351 10.1626L3.47031 11.2915L3.88659 13.6626L6.27031 13.326L8.00095 15L9.73159 13.326L12.1153 13.6626L12.5316 11.2915L14.6584 10.1626L13.601 8L14.6584 5.8367L12.5316 4.70777L12.1153 2.3367L9.73159 2.67404ZM4.4518 8.315L7.00457 10.8678L11.2366 6.24628L10.2335 5.31766L6.95691 8.8884L5.41393 7.34543L4.4518 8.315Z"
												fill="url(#paint0_linear_1517_5630)"
											/>
											<path
												fillRule="evenodd"
												clipRule="evenodd"
												d="M9.56626 3.18386L8.00169 1.66992L6.43712 3.18386L4.28126 2.87854L3.9052 5.02322L1.98169 6.04343L2.93786 7.99971L1.98169 9.95599L3.9052 10.9762L4.28126 13.1209L6.43712 12.8156L8.00169 14.3295L9.56626 12.8156L11.7221 13.1209L12.0982 10.9762L14.0217 9.95599L13.0655 7.99971L14.0217 6.04343L12.0982 5.02322L11.7221 2.87854L9.56626 3.18386ZM4.4518 8.31471L7.00531 10.8675L11.2373 6.24599L10.2342 5.31737L6.95765 8.88811L5.41467 7.34514L4.4518 8.31471Z"
												fill="url(#paint1_linear_1517_5630)"
											/>
											<path
												fillRule="evenodd"
												clipRule="evenodd"
												d="M4.4517 8.31502L7.00521 10.8678L11.2127 6.27311L11.241 6.24854L11.2372 7.2896L7.00521 11.9111L4.4517 9.35758L4.44946 8.3113L4.45319 8.31353L4.4517 8.31502Z"
												fill="#D18800"
											/>
											<defs>
												<linearGradient
													id="paint0_linear_1517_5630"
													x1="3.09425"
													y1="1.66649"
													x2="13.2755"
													y2="15.8251"
													gradientUnits="userSpaceOnUse"
												>
													<stop stopColor="#F4E72A" />
													<stop offset="0.539" stopColor="#CD8105" />
													<stop offset="0.68" stopColor="#CB7B00" />
													<stop offset="1" stopColor="#F4E72A" />
												</linearGradient>
												<linearGradient
													id="paint1_linear_1517_5630"
													x1="3.79797"
													y1="2.33641"
													x2="11.9939"
													y2="14.0562"
													gradientUnits="userSpaceOnUse"
												>
													<stop stopColor="#F9E87F" />
													<stop offset="0.406" stopColor="#E2B719" />
													<stop offset="0.989" stopColor="#E2B719" />
												</linearGradient>
											</defs>
										</svg>
									</div>
									<p className="font-display flex-1 text-xs font-semibold leading-4 tracking-[0.01em] text-[rgba(0,0,0,0.64)]">
										Outpace (
										<a
											href="https://x.com/outpacestudios"
											target="_blank"
											rel="noopener noreferrer"
											className="underline decoration-transparent transition hover:decoration-[rgba(0,0,0,0.64)]"
										>
											@outpacestudios
										</a>
										)
									</p>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			{/* Testimonial Card */}
			<div className="hidden w-full rounded-[20px] border border-[#ebebeb] bg-[rgba(0,0,0,0.04)] p-1 lg:block">
				<div className="flex w-full flex-col items-center justify-center gap-3 overflow-clip">
					<div className="w-full rounded-2xl border border-[#ebebeb] bg-white">
						<div className="flex w-full flex-col gap-5 overflow-clip rounded-2xl p-8">
							<p className="font-display text-sm leading-5 tracking-[0.02em] whitespace-pre-wrap text-[rgba(0,0,0,0.64)]">
								Thanks for your amazing work guys, conversions have gone up by{" "}
								<span className="font-semibold text-[rgba(0,0,0,0.88)]">
									12%
								</span>
								. Everything you&apos;ve delivered over the past few months has
								been impressive.
							</p>
							<div className="flex w-full items-center gap-3">
								<div className="h-[30px] w-[30px]">
									<Image
										src="/assets/testimonials/kian.jpg"
										alt="Kiarash 'Hoss' Hossainpour"
										width={30}
										height={30}
										className="rounded-full"
									/>
								</div>
								<div className="flex flex-1 flex-col gap-0.5">
									<p className="font-display text-sm leading-5 font-semibold tracking-[0.01em] whitespace-pre-wrap text-[rgba(0,0,0,0.88)]">
										Kiarash &apos;Hoss&apos; Hossainpour
									</p>
									<p className="font-display text-xs leading-4 tracking-[0.01em] whitespace-pre-wrap text-[rgba(0,0,0,0.48)]">
										Entrepreneur, Investor, #1 Best-selling author
									</p>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}
